import { useState, useEffect, useRef } from "react";
import type { AdminProduct } from "@/hooks/useAdminStore";
import type { Product } from "@/lib/api";
import { uploadImages } from "@/lib/api";

interface ProductFormModalProps {
  product?: AdminProduct | null;
  onSave: (data: Omit<Product, "id" | "rating" | "reviewCount">) => void;
  onUpdate: (id: string, fields: Partial<AdminProduct>) => void;
  onClose: () => void;
}

const CATEGORIES = ["clothes", "shoes", "bags", "nails"] as const;
const DEFAULT_SIZES = ["XS", "S", "M", "L", "XL"];

export default function ProductFormModal({
  product,
  onSave,
  onUpdate,
  onClose,
}: ProductFormModalProps) {
  const isEdit = !!product;

  const [name, setName] = useState(product?.name ?? "");
  const [category, setCategory] = useState<Product["category"]>(product?.category ?? "clothes");
  const [price, setPrice] = useState(String(product?.price ?? ""));
  const [originalPrice, setOriginalPrice] = useState(String(product?.originalPrice ?? ""));
  const [shortDescription, setShortDescription] = useState(product?.shortDescription ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [material, setMaterial] = useState(product?.material ?? "");
  const [fit, setFit] = useState(product?.fit ?? "");
  const [isNew, setIsNew] = useState(product?.isNew ?? false);
  const [isFeatured, setIsFeatured] = useState(product?.isFeatured ?? false);
  const [images, setImages] = useState<string[]>(product?.images ?? []);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Product name is required";
    if (!price || isNaN(Number(price)) || Number(price) <= 0) errs.price = "Valid price required";
    if (originalPrice && (isNaN(Number(originalPrice)) || Number(originalPrice) <= 0)) {
      errs.originalPrice = "Must be a valid number";
    }
    if (!shortDescription.trim()) errs.shortDescription = "Short description required";
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

    if (isEdit && product) {
      const fields: Partial<AdminProduct> = {
        name: name.trim(),
        price: Number(price),
        originalPrice: originalPrice ? Number(originalPrice) : undefined,
        shortDescription: shortDescription.trim(),
        isNew,
        isFeatured,
      };
      if (images.length > 0) fields.images = images;
      onUpdate(product.id, fields);
    } else {
      onSave({
        name: name.trim(),
        slug,
        category,
        price: Number(price),
        originalPrice: originalPrice ? Number(originalPrice) : undefined,
        images: images.length > 0 ? images : [
          `https://readdy.ai/api/search-image?query=luxury%20fashion%20product%20$%7Bname%7D%20elegant%20minimal%20background&width=800&height=1000&seq=admin_${Date.now()}&orientation=portrait`,
        ],
        shortDescription: shortDescription.trim(),
        description: description.trim() || shortDescription.trim(),
        material: material.trim() || "Premium quality material",
        fit: fit.trim() || "Standard fit",
        occasions: ["Date Night", "Evening Event"],
        sizes: DEFAULT_SIZES,
        colors: [{ name: "Classic", hex: "#1A1A1A" }],
        isNew,
        isFeatured,
        fabricLabel: "Premium",
      });
    }
    onClose();
  };

  const field = (
    label: string,
    key: string,
    input: React.ReactNode
  ) => (
    <div>
      <label className="block text-xs tracking-[0.12em] text-[#888] uppercase mb-1.5">
        {label}
      </label>
      {input}
      {errors[key] && (
        <p className="text-xs text-red-500 mt-1">{errors[key]}</p>
      )}
    </div>
  );

  const inputClass = (key: string) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] outline-none transition-colors ${
      errors[key] ? "border-red-400 bg-red-50" : "border-[#e0e0e0] focus:border-[#1a1a1a]"
    }`;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#f0f0f0]">
          <h2 className="font-['Playfair_Display'] text-lg text-[#1a1a1a]">
            {isEdit ? "Edit Product" : "Add New Product"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5f5f5] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg text-[#888]"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          {field("Product Name *", "name",
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass("name")}
              placeholder="e.g. Shimmer Velvet Mini Dress"
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            {!isEdit && field("Category *", "category",
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as Product["category"])}
                className={inputClass("category")}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            )}

            {field("Price ($) *", "price",
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className={inputClass("price")}
                placeholder="49"
                min="0"
                step="0.01"
              />
            )}

            {field("Original Price ($)", "originalPrice",
              <input
                type="number"
                value={originalPrice}
                onChange={(e) => setOriginalPrice(e.target.value)}
                className={inputClass("originalPrice")}
                placeholder="65 (leave empty if no sale)"
                min="0"
                step="0.01"
              />
            )}
          </div>

          {field("Short Description *", "shortDescription",
            <input
              type="text"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              className={inputClass("shortDescription")}
              placeholder="One sentence that captures the essence"
              maxLength={120}
            />
          )}

          {!isEdit && (
            <>
              {field("Description", "description",
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={`${inputClass("description")} resize-none`}
                  rows={3}
                  placeholder="Full product description..."
                  maxLength={500}
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                {field("Material", "material",
                  <input
                    type="text"
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                    className={inputClass("material")}
                    placeholder="e.g. 100% Satin Polyester"
                  />
                )}
                {field("Fit / Sizing", "fit",
                  <input
                    type="text"
                    value={fit}
                    onChange={(e) => setFit(e.target.value)}
                    className={inputClass("fit")}
                    placeholder="e.g. True to size, midi length"
                  />
                )}
              </div>
            </>
          )}

              {/* 图片上传 */}
              <div>
                <label className="block text-xs tracking-[0.12em] text-[#888] uppercase mb-1.5">
                  Product Images
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {images.map((url, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden bg-[#f5f5f5] group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                        className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-white text-sm"></i>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-16 h-16 border-2 border-dashed border-[#ddd] rounded-lg flex flex-col items-center justify-center text-[#bbb] hover:border-[#999] hover:text-[#999] cursor-pointer transition-colors disabled:opacity-50"
                  >
                    {uploading ? (
                      <i className="ri-loader-4-line text-lg animate-spin"></i>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-line text-lg"></i>
                        <span className="text-[9px] mt-0.5">Upload</span>
                      </>
                    )}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    setUploading(true);
                    try {
                      const urls = await uploadImages(files);
                      setImages((prev) => [...prev, ...urls]);
                    } catch (err) {
                      alert((err as Error).message);
                    } finally {
                      setUploading(false);
                      e.target.value = "";
                    }
                  }}
                />
                <p className="text-[10px] text-[#bbb]">Support jpg/png/webp, max 10MB each, up to 10 images</p>
              </div>

          {/* Toggles */}
          <div className="flex gap-6 pt-1">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <div
                onClick={() => setIsNew(!isNew)}
                className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${
                  isNew ? "bg-[#1a1a1a]" : "bg-[#ddd]"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    isNew ? "translate-x-5" : ""
                  }`}
                />
              </div>
              <span className="text-sm text-[#555]">Mark as New</span>
            </label>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <div
                onClick={() => setIsFeatured(!isFeatured)}
                className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${
                  isFeatured ? "bg-[#1a1a1a]" : "bg-[#ddd]"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    isFeatured ? "translate-x-5" : ""
                  }`}
                />
              </div>
              <span className="text-sm text-[#555]">Featured Product</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-[#f0f0f0]">
          <button
            onClick={onClose}
            className="flex-1 border border-[#e0e0e0] rounded-lg py-2.5 text-sm text-[#555] hover:bg-[#f9f9f9] cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 bg-[#1a1a1a] text-white rounded-lg py-2.5 text-sm hover:bg-[#333] cursor-pointer transition-colors whitespace-nowrap"
          >
            {isEdit ? "Save Changes" : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
}
